<script lang="ts">
	import { beforeUpdate, onDestroy, onMount, createEventDispatcher } from 'svelte';
	import { push } from 'svelte-spa-router';
	import { format } from '../../../services/i18n';

	import {
		CardGrid,
		Modal,
		Button,
		Text,
		Toolbar,
		Breadcrumbs,
		Input,
		Container,
		toast,
	} from '@Shelly/shelly-design-system';

	import AddEditButtons from '../../AddEditButtons.svelte';
	import ImageList from '../ImageList.svelte';
	import EmptyList from '../EmptyList.svelte';
	import ListEmptyStateSurface from '../ListEmptyStateSurface.svelte';
	// import EmptyDeviceList from '../EmptyDeviceList.svelte';

	import {
		isWallDisplay,
		is_gen_2,
		load_image_from,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { getExtSensorsStatus } from '../../../helpers/ext_sensor_helpers';

	import { edit } from 'src/stores/ui/edit_lists';
	import { createMemberStatusSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { resetRoomMainFilter, roomMainFilter } from 'src/stores/ui/filters';

	import {
		requestReorderDevice,
		requestSaveRoom,
		requestDeleteRoom,
		getImages,
		getImagesByType,
		onStartDrag,
		onEndDrag,
		resetWizardItems,
		updateWizardStepOnWizardItem,
		updateWizardStepOnPrevNext,
		scrollToElement,
		requestDeleteRoomThermostats,
		transferDevicesToRoom,
	} from '../list-helpers';
	import { HiddenDevicesRoomID } from 'src/helpers/service_rooms_helpers';

	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';
	import { get } from 'svelte/store';
	import { scenes } from 'src/stores/lists/scenes';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import RoomsListContent from './RoomsListContent.svelte';
	import EditRoomWizard from './EditRoomWizard/EditRoomWizard.svelte';
	import DeleteRoomModal from './DeleteRoomModal/DeleteRoomModal.svelte';
	import RoomsToolbar from './RoomsToolbar.svelte';
	import {
		clearRoomsItemNewFlag,
		onItemSaved,
		orderItemsInRoom,
	} from 'src/helpers/add-entity-helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import {
		getSharedHomeEmptyListCopy,
		type SharedHomeManagedContext,
	} from 'src/helpers/houses/shared_home_messaging_helpers';
	import { createAsyncAction } from 'src/helpers/async_action_guard';

	const dispatch = createEventDispatcher();
	const { loading: isSaving, run: runSave } = createAsyncAction();

	export let list;
	export let type = 'rooms';
	export let id = '-1';

	/**
	 * Loging the id here to remove some prop passing warnings.
	 * Will aither be reworked at a later point or at least,
	 * be a reminder that roomless are with id -1
	 **/
	console.log('Rooms list, id is always:', id);

	const requestType = 'room';
	const flipDurationMs = 0;

	let modalRef: Modal = null;
	let wizardItemsContainerRef: HTMLElement = null;
	let wizardContainerRef: HTMLElement = null;
	let showDeleteRoomModal = false;
	let showSharedManagedModal = false;
	let sharedManagedContext: SharedHomeManagedContext = 'add_room';
	let dragDisabled = true;
	let currentRoute = '';
	let editRoom: any = {
		name: undefined,
		image: '',
	};
	let uploadedImagesDB = [];
	let imagesByRoomType = [];
	let roomNameValidationMsg = '';
	let selectedSensorRef: HTMLElement = null;
	let allCardsRefs;
	let scrolledToView = false;
	let selectedTransferRoom = null;
	let showAddEditRoomModal = false;

	$: disabled = editRoom?.name?.trim() === '' || typeof editRoom.name === 'undefined';
	$: items = $edit && currentRoute === type ? items : [...list];
	$: dndSettings = {
		items,
		flipDurationMs: flipDurationMs,
		dragDisabled: dragDisabled,
	};
	$: (activeStep.activeIndex, (scrolledToView = false));

	let wizardItems = [
		{
			active: true,
			activeIndex: 0,
			disabled: false,
			title: $format('wizard_items_details', { default: 'Details' }),
			showMoreDots: false,
			stepNumber: 1,
			tooltipTitle: $format('wizard_items_details', { default: 'Details' }),
		},
		{
			active: false,
			activeIndex: 1,
			disabled: false,
			title: $format('wizard_items_main_sensor', { default: 'Main sensor' }),
			showMoreDots: false,
			stepNumber: 2,
			tooltipTitle: $format('wizard_items_main_sensor', {
				default: 'Main sensor',
			}),
		},
	];

	let activeStep = wizardItems[0];
	$: isLastStepWizard = activeStep.activeIndex === wizardItems.length - 1;
	$: selectedSensorRef = allCardsRefs?.[editRoom?.main_sensor];
	$: editRoomSensorIds =
		editRoom?.id != null
			? Object.values($devices)
					.filter(
						(device: Device) =>
							device.room_id === editRoom.id &&
							(device.category === 'sensor' ||
								device.category === 'thermostat' ||
								isWallDisplay(device.type)),
					)
					.map((device) => device.id)
			: [];
	$: memberStatusStore = createMemberStatusSnapshot(editRoomSensorIds);
	$: memberStatus = $memberStatusStore;

	onMount(() => {
		currentRoute = type;
		imagesByRoomType = getImagesByType(requestType, null);
	});

	beforeUpdate(() => {
		if (!$edit) handleEndDrag();

		if (currentRoute !== type) {
			currentRoute = type;
		}
	});

	onDestroy(() => {
		// check and disable edit mode when exiting the rooms list
		if ($edit) {
			edit.set(false);
		}
		resetRoomMainFilter();
		clearRoomsItemNewFlag();
	});

	const clearData = () => {
		editRoom = {
			name: undefined,
			image: '',
		};
		roomNameValidationMsg = '';
	};

	const onCloseAddEditModal = () => {
		wizardItems = resetWizardItems(wizardItems);
		activeStep = wizardItems[0];
		showAddEditRoomModal = false;
		clearData();
	};

	const onCloseDeleteModal = () => {
		showDeleteRoomModal = false;
		clearData();
	};

	const onCardClick = (item) => {
		if ($edit) return;
		setSidePanelState({ type: 'room', id: item.id });
		return push(`/home/room/${item.id}/devices`);
	};

	const validateRoomName = (room) => {
		const isEmpty = room.name === '';
		// roomNameValidationMsg = isEmpty
		// 	? $format('input_error_empty_name', { default: 'Name cannot be empty' })
		// 	: '';

		return {
			isValid: !isEmpty,
		};
	};

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editRoom = {
			...editRoom,
			name: target.value,
		};
	};

	const onCardEdit = async (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		editRoom = { ...item };
		onShowAddEditModal();
	};

	const onCardDelete = (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		editRoom = { ...item };
		showDeleteRoomModal = true;
	};

	const updateRoomItems = (currentEditRoom) => {
		items = items.map((item) => {
			if (item.id === currentEditRoom.id) {
				return currentEditRoom;
			}
			return item;
		});
	};

	const setRoomsEditChanges = (currentEditRoom) => {
		if ($edit) {
			updateRoomItems(currentEditRoom);
		} else {
			items = [...items, currentEditRoom];
		}
	};

	const handleAddEditRoomFinish = () =>
		runSave(async () => {
			editRoom.name = editRoom.name.trim();
			let currentEditRoom = {
				...editRoom,
			};

			if (!$edit) {
				await orderItemsInRoom(currentEditRoom, 'rooms');

				currentEditRoom = {
					...editRoom,
					main_sensor: false,
					overview_style: false,
					position: 0, // New items should be added at the first position to eliminate the need to scroll when adding a new item
				};
			}

			if (!validateRoomName(currentEditRoom).isValid) {
				return;
			}

			await requestSaveRoom(currentEditRoom, $edit);

			if (!$edit) {
				//Redirect to all rooms after create new
				onItemSaved(currentEditRoom, 'rooms');
			}

			// When we are on edit mode items are not reactive
			setRoomsEditChanges(currentEditRoom);

			onCloseAddEditModal();
		});

	const onDeleteRoom = async () => {
		// Re-home devices that live in this room BEFORE deleting it. The cloud
		// `interface/room/delete` endpoint does not reliably honor the `dest`
		// hint, so we explicitly bulk-update the affected devices (to the picked
		// transfer room, or to Hidden devices by default) and also patch the
		// local `devices` store so the UI updates immediately. See FE-1651.
		const transferRes = await transferDevicesToRoom(
			editRoom.id,
			selectedTransferRoom ?? HiddenDevicesRoomID,
		);

		if (!transferRes.isok) {
			return toast.open({
				type: 'danger',
				title: $format('text_failed_transfer', {
					default: 'Failed to transfer devices',
				}),
			});
		}

		const res = await requestDeleteRoom(editRoom.id, selectedTransferRoom);

		// When we are on edit mode items are not reactive
		items = items.filter((room) => room.id !== editRoom.id);

		if (res.isok) {
			let thermostatIds = [];
			Object.values($temp_schemes).forEach(
				(temp: { _meta: { [key: string]: any }; id: number }) => {
					if (temp._meta.room === editRoom.id) thermostatIds.push(temp.id.toString());
				},
			);
			if (thermostatIds.length > 0) {
				const res = await requestDeleteRoomThermostats(thermostatIds);
				if (res.isok) {
					thermostatIds.forEach((id) => {
						scenes.remove(+id);
					});
					resetDrawer();
				} else {
					toast.open({
						type: 'danger',
						title: $format('alert_failed_delete_room_thermostats', {
							default: 'Failed to delete the room thermostats',
						}),
					});
				}
			}
			rooms.remove(editRoom.id);
		} else {
			return toast.open({
				type: 'danger',
				title:
					get(format)(`type_del_err_toast`, {
						values: { this_type: get(format)(`process_delete_room`) },
					}) || `Failed to delete room`,
			});
		}
		onCloseDeleteModal();
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;

			editedList[v.id] = {
				...v,
				position: i,
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDevice(requestType, editedList);
	};

	// Image List Functions
	const onSelectImage = (e: CustomEvent) => {
		editRoom = {
			...editRoom,
			image: e.detail.image,
			backgroundColor: e.detail.backgroundColor,
		};
	};

	const onUploadImage = (e: CustomEvent) => {
		onSelectImage(e);
		uploadedImagesDB = [...e.detail.uploadedImagesDB];
	};

	const getRoomSensorDevices = (roomID) => {
		const roomSensorDevices: Device[] = Object.values($devices)
			.filter(
				(d: Device) =>
					(roomID == -1 || d.room_id == roomID) &&
					(d.category === 'sensor' ||
						d.category === 'thermostat' ||
						isWallDisplay(d.type) ||
						(is_gen_2(d)
							? getAddonStatus(d).some((addon) => {
									return addon?.sensor?.type !== 'switch';
								})
							: getExtSensorsStatus(d).length)),
			)
			.map((device) => {
				return {
					...device,
					name: getSelectCardDeviceName(device),
				};
			});

		if (roomSensorDevices.length > 0) {
			roomSensorDevices.unshift({
				id: false,
				name: 'None',
				icon: 'fal fa-ban',
			} as unknown as Device);
		}

		return roomSensorDevices;
	};

	const getSelectCardDeviceName = (device) => {
		return (
			device.name +
			(!(memberStatus[real_id(device.id)] as { online?: boolean })?.online ? ' - Offline' : '')
		);
	};

	const onSelectMainSensor = (deviceID) => {
		editRoom = {
			...editRoom,
			main_sensor: deviceID,
		};
	};

	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		let elementRef = null;
		let wizardStepIndex = 0;

		if (activeStep.activeIndex === 1 && selectedSensorRef && !scrolledToView) {
			elementRef = selectedSensorRef;
			wizardStepIndex = 1;
		}

		if (!elementRef) {
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	const onShowAddEditModal = () => {
		if (isActiveHomeShared($homes.list)) {
			sharedManagedContext = 'add_room';
			showSharedManagedModal = true;
			return;
		}
		showAddEditRoomModal = true;
	};

	const fetchUploadedImadesDB = async () => {
		uploadedImagesDB = await getImages(requestType);
	};

	$: if (showAddEditRoomModal) {
		fetchUploadedImadesDB();
	}
</script>

{#if $showNewLayout}
	<RoomsToolbar itemsLength={items.length} {onShowAddEditModal} />
{:else}
	<Toolbar class="mb-m" isSticky>
		<Breadcrumbs
			aria-label={$format('label_rooms', { default: 'Rooms' })}
			slot="breadcrumbs"
			links={[{ label: capFirstChar($format('tab_rooms', { default: 'Rooms' })) }]}
		/>
		<svelte:fragment slot="leftSide">
			<Input
				class="width-md-m text-capitalize-placeholder"
				bind:value={$roomMainFilter}
				type="text"
				label={capFirstChar($format('search', { default: 'Search' }))}
				icon="fas fa-search"
				id="rooms-toolbar-search-input"
				aria-labelledby="rooms-toolbar-search-input"
			/>
		</svelte:fragment>
		<svelte:fragment slot="rightSide">
			<AddEditButtons
				on:add_click={async () => {
					onShowAddEditModal();
				}}
				interceptEditClick={isActiveHomeShared($homes.list)}
				on:edit_click={() => {
					sharedManagedContext = 'edit_entity';
					showSharedManagedModal = true;
				}}
				disableEditButton={items.length <= 0}
			/>
		</svelte:fragment>
	</Toolbar>
{/if}

<!-- {#if isEmptyDeviceList}
	<EmptyDeviceList />
{:else  -->
{#if items.length <= 0 && !!$roomMainFilter}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-magnifying-glass"
			title={$format('no_such_room_found', { default: 'No such rooms found.' })}
			description={$format('check_the_spelling_or_try_new_search', {
				default: 'Check the spelling or try a new search.',
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0 && isActiveHomeShared($homes.list)}
	{@const sharedEmptyCopy = getSharedHomeEmptyListCopy('rooms')}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-door-open"
			title={$format(sharedEmptyCopy.titleKey, { default: sharedEmptyCopy.titleDefault })}
			description={$format(sharedEmptyCopy.descrKey, {
				default: sharedEmptyCopy.descrDefault,
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-door-open"
			title={$format('no_rooms_title', { default: 'There are no rooms yet' })}
			description={$format('no_rooms_descr', {
				default: 'Try creating a rooms for grouping of your devices for your convenience.',
			})}
			btnText={$format('button_add_room', {
				default: 'Add room',
			})}
			onClick={onShowAddEditModal}
		/>
	</ListEmptyStateSurface>
{:else}
	<CardGrid
		class={$edit && $showNewLayout ? 'mt-s' : ''}
		editMode={$edit}
		xl={350}
		lg={290}
		sm={270}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as item, _ (item)}
			<div
				class:dnd-placeholder={item.isDndShadowItem}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_room_to_reorder', {
					default: 'Drag room to reorder',
				})}
			>
				<RoomsListContent
					{item}
					{load_image_from}
					{onCardClick}
					{onCardEdit}
					{onCardDelete}
					on:handle-drag-start={(e) => onStartDrag(e, $edit, handleStartDrag, true)}
					on:handle-drag-end={(e) => onEndDrag(e, $edit, handleEndDrag, true)}
					on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
					on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
				/>
			</div>
		{/each}
	</CardGrid>
{/if}

{#if !$edit}
	<Modal
		on:close={onCloseAddEditModal}
		showModal={showAddEditRoomModal}
		title={$format('prompt_title_add_room', { default: 'Add room' })}
		removeScrollPadding
	>
		<div slot="content">
			<Container noScroll padding="scrollPaddings">
				<Text element="h2" class="mb-s">
					{$format('room_name', { default: 'Room Name' })}
				</Text>
				<Input
					aria-labelledby="input-8eumvbr"
					icon="fas fa-door-open"
					label={$format('label_name', { default: 'name' })}
					value={editRoom.name}
					error={editRoom.name === ''
						? $format('input_error_empty_name', {
								default: 'Name cannot be empty',
							})
						: ''}
					on:input={onInputName}
					on:enter={() => dispatch('enter')}
					id="input-8eumvbr"
				/>
				<hr class="mt-l mb-l" />
			</Container>

			<!-- DO NOT REMOVE COMMENT: Padding references -->
			<Container class="mb-s" noScroll padding="scrollPaddings">
				<Text element="h2" class="mb-m">
					{$format('select_image', { default: 'Select image' })}
				</Text>

				<ImageList
					{uploadedImagesDB}
					imagesByTypeDB={imagesByRoomType}
					selectedImage={editRoom.image}
					selectedBackgroundColor={editRoom.backgroundColor}
					setDefaultImage={!$edit && showAddEditRoomModal}
					{requestType}
					on:select-image={onSelectImage}
					on:upload-image={onUploadImage}
				/>
			</Container>
		</div>
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={onCloseAddEditModal}
				aria-label={$format('aria_cancel', { default: 'Cancel' })}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				loading={$isSaving}
				on:click={handleAddEditRoomFinish}
				{disabled}>{$format('button_save', { default: 'Save' })}</Button
			>
		</div>
	</Modal>
{:else}
	<EditRoomWizard
		bind:showEditRoomModal={showAddEditRoomModal}
		id={editRoom?.id}
		{type}
		{list}
		{currentRoute}
		on:edited-room={(e) => {
			clearData();
			updateRoomItems(e.detail);
		}}
	/>
{/if}

<DeleteRoomModal
	bind:showDeleteRoomModal
	id={editRoom?.id}
	{type}
	{list}
	{currentRoute}
	on:delete={(e) => {
		clearData();
		items = e.detail;
	}}
/>

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context={sharedManagedContext} />
